/**
 * @qd/questions — ISO/IEC/IEEE 29119-3 question bank.
 *
 * Tier 1 (organizational test process documentation) is wired in this round:
 * 35 questions covering Test Policy and Organizational Test Practices, sourced
 * from the official 29119-3:2021 preview, ISTQB CTAL-TM v2012, and Stuart Reid
 * (WG26). See `tier1.json` for the canonical data.
 *
 * Tier 2 (test management) and Tier 3 (dynamic test process) are scaffolded as
 * empty arrays for Phase 6 round 2; see `README.md` adjacent to this file for
 * the document-type matrix that will populate them.
 */

import tier1Raw from './tier1.json';
import tier2Raw from './tier2.json';
import tier3Raw from './tier3.json';

export interface Question {
  readonly id: string;
  readonly tier: 1 | 2 | 3;
  readonly documentType: string;
  readonly kind: 'multiple-choice' | 'true-false' | 'scenario';
  readonly difficulty: 'easy' | 'medium' | 'hard';
  readonly prompt: string;
  readonly choices?: readonly string[];
  readonly correctIndex: number | boolean;
  readonly explanation: string;
  readonly isoClauseRef: string;
  readonly sourceUrls: readonly string[];
  readonly needsPaidStandardReview?: boolean;
}

export const tier1Questions: readonly Question[] = tier1Raw as readonly Question[];
export const tier2Questions: readonly Question[] = tier2Raw as readonly Question[];
export const tier3Questions: readonly Question[] = tier3Raw as readonly Question[];

export const allQuestions: readonly Question[] = [
  ...tier1Questions,
  ...tier2Questions,
  ...tier3Questions,
];

export function questionsForTier(tier: 1 | 2 | 3): readonly Question[] {
  switch (tier) {
    case 1:
      return tier1Questions;
    case 2:
      return tier2Questions;
    case 3:
      return tier3Questions;
  }
}

/**
 * Backwards-compatible alias for the previous Phase-0 scaffold name. New code
 * should prefer `allQuestions` directly.
 *
 * @deprecated use `allQuestions`.
 */
export const PLACEHOLDER_QUESTIONS: readonly Question[] = allQuestions;
